import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { formatGameRecap, shareRecap } from 'utils/shareRecap';

const ConfirmSaveModal = ({ isOpen, onConfirm, onCancel, isSaving, selectedCount, gameDate, location, savedSuccessfully, winners, losers, winnersWithStats, losersWithStats, winnersScore, losersScore, isEditMode }) => {
  const [shareStatus, setShareStatus] = useState(null);

  if (!isOpen) return null;

  const handleShare = async () => {
    setShareStatus('sharing');
    const text = formatGameRecap({ date: gameDate, location, winners: winnersWithStats || [], losers: losersWithStats || [], winnersScore, losersScore });
    const result = await shareRecap(text, () => setShareStatus('copied'));
    if (result === 'shared') setShareStatus(null);
    if (result === 'copied') {
      setTimeout(() => setShareStatus(null), 2500);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
        style={{ backgroundColor: 'var(--color-card)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-950/30 flex items-center justify-center flex-shrink-0">
            <Icon name="Save" size={20} color="var(--color-primary)" />
          </div>
          <h2 id="confirm-modal-title" className="font-heading font-bold text-lg" style={{ color: 'var(--color-foreground)' }}>
            {savedSuccessfully ? (isEditMode ? 'Game Updated!' : 'Game Saved!') : (isEditMode ? 'Update Game Record?' : 'Save Game Record?')}
          </h2>
        </div>

        <div className="space-y-2 mb-6 p-4 rounded-xl bg-[var(--color-muted)]">
          <div className="flex justify-between font-caption text-sm gap-2">
            <span className="flex items-center gap-1.5 flex-shrink-0" style={{ color: 'var(--color-muted-foreground)' }}>
              <Icon name="Trophy" size={13} color="#059669" />
              Winners
            </span>
            <span className="font-semibold text-emerald-600 text-right truncate">
              {winners?.length > 0 ? winners?.join(', ') : '—'}
            </span>
          </div>
          <div className="flex justify-between font-caption text-sm gap-2">
            <span className="flex items-center gap-1.5 flex-shrink-0" style={{ color: 'var(--color-muted-foreground)' }}>
              <Icon name="ThumbsDown" size={13} color="#DC2626" />
              Losers
            </span>
            <span className="font-semibold text-red-600 text-right truncate">
              {losers?.length > 0 ? losers?.join(', ') : '—'}
            </span>
          </div>
          <div className="flex justify-between font-caption text-sm">
            <span style={{ color: 'var(--color-muted-foreground)' }}>Date</span>
            <span className="font-semibold" style={{ color: 'var(--color-foreground)' }}>
              {gameDate ? new Date(gameDate + 'T00:00:00')?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—'}
            </span>
          </div>
          <div className="flex justify-between font-caption text-sm">
            <span style={{ color: 'var(--color-muted-foreground)' }}>Players</span>
            <span className="font-semibold" style={{ color: 'var(--color-foreground)' }}>{selectedCount}</span>
          </div>
        </div>

        {savedSuccessfully ? (
          <div className="space-y-3">
            {shareStatus === 'copied' && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200">
                <Icon name="CheckCircle" size={14} color="#059669" />
                <span className="font-caption text-xs text-emerald-700">Recap copied to clipboard!</span>
              </div>
            )}
            <Button
              variant="default"
              fullWidth
              onClick={handleShare}
              iconName="Share2"
              iconPosition="left"
              loading={shareStatus === 'sharing'}
            >
              Share Recap
            </Button>
            <Button variant="outline" fullWidth onClick={onCancel}>
              Close
            </Button>
          </div>
        ) : (
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="default" fullWidth onClick={onConfirm} loading={isSaving} iconName="Save" iconPosition="left">
              {isEditMode ? 'Update Game' : 'Save Game'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmSaveModal;